import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Crown, Users, ChevronDown, ChevronRight, Heart, Shield, Sparkles } from "lucide-react";

const fmtUSD = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(n || 0));

const fmtPct = (n) => {
  if (n === null || n === undefined) return "0";
  const num = Number(n);
  if (Number.isNaN(num)) return "0";
  return num.toFixed(4);
};

const ROLE_TONE = {
  sovereign: { color: "#F5D87E", border: "border-[#D4AF37]/60", bg: "bg-[#D4AF37]/10", label: "SOVEREIGN" },
  admiral: { color: "#67E8F9", border: "border-cyan-400/40", bg: "bg-cyan-400/10", label: "ADMIRAL" },
  junior_admiral: { color: "#FCA5A5", border: "border-rose-400/40", bg: "bg-rose-400/10", label: "HEIR" },
  lineage_kin: { color: "#FBBF24", border: "border-amber-400/40", bg: "bg-amber-400/10", label: "KIN" },
  guardian: { color: "#C4B5FD", border: "border-purple-400/40", bg: "bg-purple-400/10", label: "GUARDIAN" },
};

function NodeIcon({ role, color }) {
  if (role === "sovereign") return <Crown className="w-4 h-4" style={{ color }} />;
  if (role === "guardian") return <Heart className="w-4 h-4" style={{ color }} />;
  if (role === "junior_admiral") return <Sparkles className="w-4 h-4" style={{ color }} />;
  return <Shield className="w-4 h-4" style={{ color }} />;
}

// Walk the tree and produce a flat list of {node, depth, hasChildren} for
// iterative rendering. This avoids self-referencing JSX components which
// can blow up babel-traverse in some CRA configs.
function flattenTree(roots, collapsed) {
  const out = [];
  const walk = (node, depth) => {
    const hasChildren = Array.isArray(node.children) && node.children.length > 0;
    out.push({ node, depth, hasChildren });
    if (!hasChildren) return;
    if (collapsed[node.id]) return;
    for (const c of node.children) walk(c, depth + 1);
  };
  for (const r of roots || []) walk(r, 0);
  return out;
}

function NodeRow({ node, depth, hasChildren, isCollapsed, onToggle }) {
  const tone = ROLE_TONE[node.role] || ROLE_TONE.lineage_kin;
  const v = node.vault;
  const showVault =
    v &&
    (Number(v.ancestral_share_pct) > 0 ||
      Number(v.live_share_pct) > 0 ||
      Number(v.ancestral_balance_cents) > 0 ||
      Number(v.live_balance_cents) > 0);

  return (
    <div
      data-testid={"lineage-node-" + node.id}
      style={{ marginLeft: depth * 28 }}
      className={"relative rounded-xl border " + tone.border + " " + tone.bg + " p-4 mb-2 transition-all duration-300"}
    >
      <div className="flex items-start gap-3">
        {hasChildren ? (
          <button
            type="button"
            onClick={onToggle}
            data-testid={"lineage-toggle-" + node.id}
            className="mt-1 flex-shrink-0 w-5 h-5 rounded flex items-center justify-center hover:bg-white/10 transition"
            aria-label={isCollapsed ? "Expand" : "Collapse"}
          >
            {isCollapsed
              ? <ChevronRight className="w-3.5 h-3.5 text-zinc-400" />
              : <ChevronDown className="w-3.5 h-3.5 text-zinc-400" />}
          </button>
        ) : (
          <div className="w-5 h-5 mt-1 flex-shrink-0" />
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <NodeIcon role={node.role} color={tone.color} />
            <span className="text-base font-bold text-white tracking-tight">{node.full_name}</span>
            <span
              className="text-[9px] tracking-armada font-bold px-1.5 py-0.5 rounded uppercase"
              style={{ color: tone.color, borderWidth: 1, borderStyle: "solid", borderColor: tone.color + "40", background: tone.color + "10" }}
            >
              {tone.label}
            </span>
            {node.relationship && (
              <span className="text-[10px] tracking-wider text-zinc-500 uppercase">
                · {String(node.relationship).replace("_", " ")}
              </span>
            )}
            {!node.is_active && (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-red-500/20 border border-red-500/40 text-red-300 uppercase tracking-wider">
                INACTIVE
              </span>
            )}
          </div>
          <div className="text-xs text-zinc-500 mt-0.5 truncate">{node.email}</div>

          {showVault && (
            <div className="mt-3 grid grid-cols-2 gap-2 max-w-md">
              <div className="px-2.5 py-1.5 rounded border border-amber-500/20 bg-amber-500/5">
                <div className="text-[9px] tracking-wider text-amber-300/80">ANCESTRAL</div>
                <div className="text-sm font-bold text-white tabular-nums">
                  {fmtUSD(v.ancestral_balance_usd)}
                  <span className="text-[10px] text-zinc-500 font-medium ml-1">· {fmtPct(v.ancestral_share_pct)}%</span>
                </div>
              </div>
              <div className="px-2.5 py-1.5 rounded border border-emerald-500/20 bg-emerald-500/5">
                <div className="text-[9px] tracking-wider text-emerald-300/80">LIVE</div>
                <div className="text-sm font-bold text-white tabular-nums">
                  {fmtUSD(v.live_balance_usd)}
                  <span className="text-[10px] text-zinc-500 font-medium ml-1">· {fmtPct(v.live_share_pct)}%</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function LineageTree() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [collapsed, setCollapsed] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/admin/lineage/tree");
        setData(res.data);
      } catch (e) {
        const detail = e && e.response && e.response.data && e.response.data.detail;
        setErr(detail || (e && e.message) || "Unknown error");
      }
    })();
  }, []);

  const rows = useMemo(() => flattenTree((data && data.roots) || [], collapsed), [data, collapsed]);

  if (err) {
    return (
      <div data-testid="lineage-tree-error" className="glass-strong rounded-xl p-6 border border-red-500/30 text-sm text-red-300 mb-8">
        Unable to load Lineage Tree · {err}
      </div>
    );
  }
  if (!data) {
    return (
      <div className="glass-strong rounded-xl p-8 border border-white/10 text-center text-xs text-zinc-500 tracking-armada mb-8">
        SCANNING DYNASTY LINEAGE…
      </div>
    );
  }

  const a = data.allocation || {};

  return (
    <div data-testid="lineage-tree" className="glass-strong rounded-2xl p-6 lg:p-8 border border-[#D4AF37]/20 mb-8">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-4 h-4 text-[#D4AF37]" />
            <div className="text-[10px] tracking-armada text-[#D4AF37] font-bold">
              LINEAGE TREE · {data.total_nodes} DYNASTY NODES
            </div>
          </div>
          <h3 className="text-2xl md:text-3xl font-black tracking-tighter text-white">The Ancestral Ledger</h3>
          <p className="text-xs text-zinc-500 mt-1">
            Recursive dynasty — Matriarch · Sovereign · Heirs · Kin · Guardians. Vault shares of the 5% Ancestral + 2% Live pools shown per node.
          </p>
        </div>

        <div className="text-right">
          <div className="text-[10px] tracking-armada text-zinc-500 mb-1">POOL ALLOCATION</div>
          <div data-testid="lineage-allocation" className="grid grid-cols-2 gap-3 min-w-[260px]">
            <div className={"px-3 py-2 rounded border " + (a.over_allocated_ancestral ? "border-red-500/40 bg-red-500/5" : "border-amber-500/30 bg-amber-500/5")}>
              <div className="text-[9px] tracking-wider text-amber-300/80">ANCESTRAL · 5%</div>
              <div className="text-sm font-bold text-white tabular-nums">
                {fmtPct(a.ancestral_total_pct)}
                <span className="text-[10px] text-zinc-500 font-medium ml-1">% allocated</span>
              </div>
              <div className="text-[9px] text-zinc-500 mt-0.5">
                overflow → treasury · {fmtPct(a.ancestral_overflow_to_treasury_pct)}%
              </div>
            </div>
            <div className={"px-3 py-2 rounded border " + (a.over_allocated_live ? "border-red-500/40 bg-red-500/5" : "border-emerald-500/30 bg-emerald-500/5")}>
              <div className="text-[9px] tracking-wider text-emerald-300/80">LIVE · 2%</div>
              <div className="text-sm font-bold text-white tabular-nums">
                {fmtPct(a.live_total_pct)}
                <span className="text-[10px] text-zinc-500 font-medium ml-1">% allocated</span>
              </div>
              <div className="text-[9px] text-zinc-500 mt-0.5">
                overflow → treasury · {fmtPct(a.live_overflow_to_treasury_pct)}%
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-white/5 pt-5">
        {rows.length === 0 ? (
          <div className="text-sm text-zinc-500 py-6 text-center border border-dashed border-white/10 rounded-md">
            No dynasty nodes yet.
          </div>
        ) : (
          rows.map((r) => (
            <NodeRow
              key={r.node.id}
              node={r.node}
              depth={r.depth}
              hasChildren={r.hasChildren}
              isCollapsed={!!collapsed[r.node.id]}
              onToggle={() => setCollapsed((c) => ({ ...c, [r.node.id]: !c[r.node.id] }))}
            />
          ))
        )}
      </div>
    </div>
  );
}
